import React, { useState, useEffect, useCallback } from "react";
import axios from "../services/httpClient";
import { toast } from "react-toastify";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { FaCalendarAlt } from "react-icons/fa";

const AnalyticsCard = ({
  title,
  apiEndpoint,
  apiType,
  aggregateType: initialAggregateType = "day",
}) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [aggregateType, setAggregateType] = useState(initialAggregateType);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        aggregate_type: aggregateType,
      });

      if (startDate) {
        params.append("start_date", startDate);
      }
      if (endDate) {
        params.append("end_date", endDate);
      }

      let url = apiEndpoint;
      if (apiType) {
        url = `${apiEndpoint}?type=${apiType}&${params.toString()}`;
      } else {
        url = `${apiEndpoint}?${params.toString()}`;
      }

      const response = await axios.get(url);
      if (response.data && response.data.data) {
        setData(response.data.data);
      } else {
        setData([]);
      }
    } catch (error) {
      console.error(`Error fetching ${title}:`, error);
      toast.error(`Failed to load ${title} data`);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [apiEndpoint, apiType, aggregateType, startDate, endDate, title]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const chartData = data.map((item) => ({
    period: item.period || "N/A",
    total: item.total_count || 0,
    unique: item.unique_user_count || 0,
  }));

  const totalSum = data.reduce((sum, item) => sum + (item.total_count || 0), 0);
  const uniqueSum = data.reduce(
    (sum, item) => sum + (item.unique_user_count || 0),
    0
  );

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">{title}</h3>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
          {/* Aggregate Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Aggregate Type
            </label>
            <select
              value={aggregateType}
              onChange={(e) => setAggregateType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/40 text-sm"
            >
              <option value="day">Day</option>
              <option value="week">Week</option>
              <option value="month">Month</option>
            </select>
          </div>

          {/* Start Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Start Date
            </label>
            <div className="relative">
              <FaCalendarAlt className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm" />
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full pl-8 pr-2 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/40 text-sm"
              />
            </div>
          </div>

          {/* End Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              End Date
            </label>
            <div className="relative">
              <FaCalendarAlt className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm" />
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full pl-8 pr-2 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/40 text-sm"
              />
            </div>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="flex gap-4 mb-4">
          <div className="flex-1 bg-blue-50 rounded-lg p-3">
            <p className="text-xs text-gray-600 mb-1">Total Count</p>
            <p className="text-xl font-bold text-blue-600">{totalSum}</p>
          </div>
          <div className="flex-1 bg-purple-50 rounded-lg p-3">
            <p className="text-xs text-gray-600 mb-1">Unique Users</p>
            <p className="text-xl font-bold text-purple-600">{uniqueSum}</p>
          </div>
        </div>
      </div>

      {/* Chart */}
      {loading ? (
        <div className="h-64 flex items-center justify-center">
          <div className="text-gray-500">Loading...</div>
        </div>
      ) : chartData.length > 0 ? (
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="period"
                tick={{ fontSize: 11 }}
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: "white",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                }}
              />
              <Legend />
              <Bar
                dataKey="total"
                fill="#0D99FF"
                name="Total Count"
                radius={[4, 4, 0, 0]}
              />
              <Bar
                dataKey="unique"
                fill="#10b981"
                name="Unique Users"
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="h-64 flex items-center justify-center">
          <div className="text-gray-500">No data available</div>
        </div>
      )}
    </div>
  );
};

const AdminDashboard = () => {
  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-gray-800 mb-6">Admin Dashboard</h1>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Card 1: Notes Upload Analytics */}
        <AnalyticsCard
          title="Notes Upload Analytics"
          apiEndpoint="/admin/user-practise-analytics"
          apiType="notes"
        />

        {/* Card 2: Goals Analytics */}
        <AnalyticsCard
          title="Goals Analytics"
          apiEndpoint="/admin/user-practise-analytics"
          apiType="goals"
        />

        {/* Card 3: Quiz Analytics */}
        <AnalyticsCard
          title="Quiz Analytics"
          apiEndpoint="/admin/user-practise-analytics"
          apiType="quiz"
        />

        {/* Card 4: Workbook Upload Analytics */}
        <AnalyticsCard
          title="Workbook Upload Analytics"
          apiEndpoint="/admin/workbook-upload-analytics"
        />

        {/* Card 5: Evaluate Worksheet Analytics */}
        <AnalyticsCard
          title="Evaluate Worksheet Analytics"
          apiEndpoint="/admin/worksheet-evaluation-analytics"
        />
      </div>
    </div>
  );
};

export default AdminDashboard;
